import React, { useId, useMemo } from 'react';
import {
  SimulationHotspotInteraction,
  SimulationMovableInteraction,
  SimulationSceneInteraction,
} from '../../../types/v2';
import { ObjectTargetControls } from './ObjectTargetControls';

interface DisplayInputPlaceholderSceneProps {
  renderState: Record<string, any>;
  interactions?: SimulationSceneInteraction[];
  disabled?: boolean;
  onAction: (actionId: string) => void;
}

export const DisplayInputPlaceholderScene: React.FC<DisplayInputPlaceholderSceneProps> = ({
  renderState,
  interactions = [],
  disabled = false,
  onAction,
}) => {
  const titleId = useId();
  const descriptionId = useId();
  const displayStatus = String(renderState.display_status || 'no_signal');
  const selectedInput = String(renderState.selected_input || 'hdmi2').toUpperCase().replace('HDMI', 'HDMI ');
  const cablePort = String(renderState.cable_port || 'hdmi1').toLowerCase();
  const powerOn = renderState.computer_power === 'on';
  const desktopVisible = displayStatus === 'desktop';
  const inputMenuVisible = renderState.input_menu_visible === true;
  const hotspots = useMemo(() => (
    Object.fromEntries(
      interactions
        .filter((item): item is SimulationHotspotInteraction => item.kind === 'hotspot')
        .map((item) => [item.id, item]),
    )
  ), [interactions]);
  const cableInteraction = interactions.find(
    (item): item is SimulationMovableInteraction => (
      item.kind === 'movable' && item.id === 'display_cable'
    ),
  );
  const dynamicDescription = [
    `The computer is ${powerOn ? 'powered on' : 'powered off'}.`,
    cablePort === 'disconnected'
      ? 'The display cable is disconnected.'
      : `The display cable is connected to ${cablePort.toUpperCase().replace('HDMI', 'HDMI ')}.`,
    `The monitor is set to ${selectedInput}.`,
    desktopVisible ? 'The desktop and original task are visible.' : 'The monitor shows No Signal.',
    inputMenuVisible ? 'The monitor input menu is open.' : 'The monitor input menu is closed.',
  ].join(' ');
  const submitHotspot = (id: string) => {
    const interaction = hotspots[id];
    if (!interaction || disabled) return;
    onAction(interaction.action_id);
  };
  return (
    <div className="simulation-scene" aria-label="Interactive placeholder workstation simulation">
      <svg
        viewBox="0 0 760 390"
        role="img"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
      >
        <title id={titleId}>Computer and monitor workstation</title>
        <desc id={descriptionId}>{dynamicDescription}</desc>
        <rect x="20" y="20" width="720" height="345" rx="18" className="simulation-room" />
        <rect x="62" y="118" width="145" height="220" rx="12" className="simulation-tower" />
        <circle cx="174" cy="150" r="9" className={powerOn ? 'simulation-led-on' : 'simulation-led-off'} />
        <rect x="88" y="185" width="92" height="8" rx="4" className="simulation-slot" />
        <rect x="88" y="212" width="92" height="8" rx="4" className="simulation-slot" />
        <text x="134" y="316" textAnchor="middle" className="simulation-label">COMPUTER</text>

        <path
          d={
            cablePort === 'hdmi1'
              ? 'M207 270 C270 270, 275 250, 342 250'
              : cablePort === 'hdmi2'
                ? 'M207 270 C270 270, 275 282, 342 282'
                : 'M207 270 C260 270, 265 320, 315 320'
          }
          className="simulation-cable"
        />
        <rect x="209" y="260" width="26" height="20" rx="3" className="simulation-port" />
        {cablePort === 'disconnected' ? (
          <rect x="304" y="310" width="24" height="18" rx="3" className="simulation-cable-end" />
        ) : null}

        <rect x="342" y="66" width="350" height="224" rx="14" className="simulation-monitor-frame" />
        <rect x="334" y="241" width="18" height="18" rx="3" className="simulation-port" />
        <rect x="334" y="273" width="18" height="18" rx="3" className="simulation-port" />
        <text x="296" y="254" className="simulation-small-label">HDMI 1</text>
        <text x="296" y="286" className="simulation-small-label">HDMI 2</text>
        <rect x="363" y="87" width="308" height="182" rx="5" className={desktopVisible ? 'simulation-screen-on' : 'simulation-screen-off'} />
        {desktopVisible ? (
          <>
            <rect x="382" y="107" width="270" height="32" rx="4" className="simulation-window-bar" />
            <rect x="382" y="151" width="118" height="96" rx="5" className="simulation-window" />
            <rect x="514" y="151" width="138" height="96" rx="5" className="simulation-window" />
            <text x="517" y="207" textAnchor="middle" className="simulation-desktop-text">ORIGINAL TASK</text>
          </>
        ) : (
          <>
            <text x="517" y="169" textAnchor="middle" className="simulation-no-signal">NO SIGNAL</text>
            <text x="517" y="198" textAnchor="middle" className="simulation-screen-caption">Check the display source</text>
          </>
        )}
        <rect x="500" y="290" width="38" height="40" className="simulation-stand" />
        <rect x="445" y="328" width="148" height="12" rx="6" className="simulation-stand" />

        {inputMenuVisible ? (
          <g>
            <rect x="535" y="105" width="115" height="106" rx="6" className="simulation-input-menu" />
            <text x="551" y="128" className="simulation-menu-title">INPUT</text>
            <rect x="546" y="141" width="92" height="25" rx="4" className={selectedInput === 'HDMI 1' ? 'simulation-menu-selected' : 'simulation-menu-row'} />
            <text x="556" y="159" className="simulation-menu-text">HDMI 1</text>
            <rect x="546" y="173" width="92" height="25" rx="4" className={selectedInput === 'HDMI 2' ? 'simulation-menu-selected' : 'simulation-menu-row'} />
            <text x="556" y="191" className="simulation-menu-text">HDMI 2</text>
          </g>
        ) : null}
      </svg>

      {hotspots.computer ? (
        <button
          type="button"
          className="simulation-hotspot simulation-hotspot-computer"
          onClick={() => submitHotspot('computer')}
          disabled={disabled}
          aria-label={hotspots.computer.label}
        >
          Inspect
        </button>
      ) : null}

      {hotspots.cable ? (
        <button
          type="button"
          className="simulation-hotspot simulation-hotspot-cable"
          onClick={() => submitHotspot('cable')}
          disabled={disabled}
          aria-label={hotspots.cable.label}
        >
          Inspect cable
        </button>
      ) : null}

      {hotspots.monitor_input ? (
        <button
          type="button"
          className="simulation-hotspot simulation-hotspot-monitor"
          onClick={() => submitHotspot('monitor_input')}
          disabled={disabled}
          aria-label={hotspots.monitor_input.label}
        >
          Input
        </button>
      ) : null}

      {inputMenuVisible && hotspots.input_hdmi1 && hotspots.input_hdmi2 ? (
        <div className="simulation-input-controls" aria-label="Monitor input menu">
          <button
            type="button"
            onClick={() => submitHotspot('input_hdmi1')}
            disabled={disabled}
            aria-pressed={selectedInput === 'HDMI 1'}
          >
            HDMI 1
          </button>
          <button
            type="button"
            onClick={() => submitHotspot('input_hdmi2')}
            disabled={disabled}
            aria-pressed={selectedInput === 'HDMI 2'}
          >
            HDMI 2
          </button>
        </div>
      ) : null}

      {cableInteraction ? (
        <ObjectTargetControls
          interaction={cableInteraction}
          currentValue={renderState[cableInteraction.state_field]}
          disabled={disabled}
          className="simulation-cable-controls"
          onAction={onAction}
        />
      ) : null}
    </div>
  );
};
